import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, Camera, X, ArrowsClockwise, SpeakerHigh } from '@phosphor-icons/react';
import { api } from '@/lib/api';

/**
 * BubbleEye: lets the owner show the bubble what they see via webcam.
 * Captures a frame, sends to the bubble, displays its first-person observation.
 */
const BubbleEye = ({ bubble, open, onClose, onObservation }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [busy, setBusy] = useState(false);
  const [obs, setObs] = useState(null);
  const [facing, setFacing] = useState('environment');
  const [past, setPast] = useState([]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    const start = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        });
        if (!active) { s.getTracks().forEach(t => t.stop()); return; }
        setStream(s);
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch (e) {
        toast.error('camera permission needed');
        onClose && onClose();
      }
    };
    start();
    api.get(`/bubbles/${bubble.id}/observations`).then(({ data }) => setPast(data.observations || []));
    return () => {
      active = false;
      if (stream) stream.getTracks().forEach(t => t.stop());
      setStream(null);
    };
  // eslint-disable-next-line
  }, [open, facing, bubble?.id]);

  const speak = (text) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    if (voices.length) {
      const idx = Array.from(bubble.name || '').reduce((a, c) => a + c.charCodeAt(0), 0) % voices.length;
      u.voice = voices[idx];
    }
    u.pitch = 1.05; u.rate = 0.96;
    window.speechSynthesis.speak(u);
  };

  const capture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setBusy(true);
    setObs(null);
    try {
      const v = videoRef.current;
      const c = canvasRef.current;
      const size = Math.min(v.videoWidth, v.videoHeight) || 480;
      c.width = size; c.height = size;
      const ctx = c.getContext('2d');
      // center-crop
      const sx = (v.videoWidth - size) / 2;
      const sy = (v.videoHeight - size) / 2;
      ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
      const dataUrl = c.toDataURL('image/jpeg', 0.7);
      const b64 = dataUrl.split(',')[1];
      const { data } = await api.post(`/bubbles/${bubble.id}/observe`, { image_b64: b64 });
      setObs(data.observation);
      setPast((arr) => [...arr, { text: data.observation, ts: data.at }]);
      speak(data.observation);
      onObservation && onObservation(data.observation);
      if (navigator.vibrate) try { navigator.vibrate(80); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'the bubble could not focus');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-xl"
        data-testid="bubble-eye-overlay"
      >
        <button onClick={onClose} data-testid="bubble-eye-close"
                className="absolute top-5 right-5 p-2 rounded-full glass-hi text-white/70 hover:text-white z-10">
          <X size={20} weight="bold" />
        </button>
        <button onClick={() => setFacing(f => f === 'user' ? 'environment' : 'user')}
                data-testid="bubble-eye-flip"
                className="absolute top-5 left-5 p-2 rounded-full glass-hi text-white/70 hover:text-white z-10">
          <ArrowsClockwise size={20} weight="bold" />
        </button>

        <div className="relative h-full w-full grid place-items-center px-4 sm:px-8">
          <div className="w-full max-w-md text-center">
            <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan mb-2 flex items-center justify-center gap-1.5">
              <Eye size={14} weight="fill" className="animate-pulse" /> {bubble.name} is watching
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">
              Show me <span className="shimmer-text">your world</span>
            </h1>

            {/* Eye-shaped viewport */}
            <div className="relative my-6 mx-auto h-72 w-72 sm:h-80 sm:w-80">
              <div className="absolute inset-0 rounded-full overflow-hidden ring-2 ring-bx-cyan/40 shadow-[0_0_60px_rgba(0,240,255,0.35)]">
                <video ref={videoRef} autoPlay muted playsInline
                       className="h-full w-full object-cover" />
                {/* Iris overlay */}
                <span className="absolute inset-0 rounded-full pointer-events-none"
                      style={{ background: 'radial-gradient(circle at 50% 50%, transparent 35%, rgba(0,0,0,0.55) 75%)' }} />
                <span className="absolute inset-0 rounded-full pointer-events-none"
                      style={{ boxShadow: 'inset 0 0 60px rgba(0,240,255,0.55)' }} />
              </div>
              {busy && (
                <span className="absolute inset-0 rounded-full border-2 border-bx-magenta animate-ping" />
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <button onClick={capture} disabled={busy} data-testid="bubble-eye-capture"
                    className="btn-volt px-6 py-3.5 rounded-full font-bold flex items-center gap-2 mx-auto disabled:opacity-50">
              <Camera size={16} weight="bold" />
              {busy ? `${bubble.name} is looking…` : 'Let it see'}
            </button>

            <AnimatePresence>
              {obs && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}
                  className="mt-6 glass rounded-2xl p-4 text-left"
                  data-testid="bubble-eye-observation"
                >
                  <div className="text-[10px] uppercase tracking-widest text-bx-cyan flex items-center gap-1">
                    <Eye size={12} weight="fill" /> {bubble.name} says
                    <button onClick={() => speak(obs)} className="ml-auto text-white/65 hover:text-white">
                      <SpeakerHigh size={12} weight="bold" />
                    </button>
                  </div>
                  <p className="font-display text-lg sm:text-xl mt-2 italic">"{obs}"</p>
                </motion.div>
              )}
            </AnimatePresence>

            {past.length > 0 && (
              <div className="mt-6 text-left">
                <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">
                  what {bubble.name} remembers
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1.5">
                  {past.slice().reverse().slice(0, 6).map((p, i) => (
                    <div key={i} className="text-xs text-white/60 italic">"{p.text}"</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BubbleEye;
